'use client';

import useSWR from 'swr';
import { mutate } from 'swr';
import { getAdminContextHeaders } from './config';
import type {
  BookingWithDetails,
  Service,
  StaffMemberWithProfile,
  Alert,
} from '@/lib/integrations/supabase/types';

// ============================================================================
// Types
// ============================================================================

export interface DashboardData {
  upcoming: {
    bookings: BookingWithDetails[];
    totalCount: number;
  };
  due: {
    bookings: BookingWithDetails[];
    totalCount: number;
  };
  services: Service[];
  staff: StaffMemberWithProfile[];
  alerts: Alert[];
  earnings: {
    total: number;
    cash: number;
    card: number;
  };
  timestamp: number;
}

// ============================================================================
// SWR Key
// ============================================================================

export const dashboardKey = (businessId: string) => ['dashboard', businessId];

// ============================================================================
// Fetcher
// ============================================================================

async function fetchDashboard(businessId: string, token: string): Promise<DashboardData> {
  const response = await fetch(`/api/dashboard`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      ...getAdminContextHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch dashboard data');
  }

  return response.json();
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Fetches all dashboard data in a single request.
 * Returns upcoming bookings, due bookings, services, staff, alerts, and earnings.
 *
 * Use this for initial page load to reduce network requests from 6-7 to 1.
 * Individual hooks can still be used for granular cache invalidation after mutations.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useDashboard(businessId, session?.access_token);
 *
 * if (isLoading) return <Skeleton />;
 *
 * const { upcoming, due, services, staff, alerts, earnings } = data;
 * ```
 */
export function useDashboard(
  businessId: string | null,
  accessToken: string | null | undefined
) {
  return useSWR<DashboardData>(
    businessId && accessToken ? dashboardKey(businessId) : null,
    () => fetchDashboard(businessId!, accessToken!),
    {
      dedupingInterval: 100, // 100ms - allow instant optimistic updates
      revalidateIfStale: false, // Don't refetch on stale - let optimistic updates show immediately
      revalidateOnMount: true,
      revalidateOnFocus: false, // Let broadcast handle updates
      keepPreviousData: false, // Show new data immediately for instant feedback
    }
  );
}

// ============================================================================
// Invalidation
// ============================================================================

/**
 * Invalidate the dashboard cache.
 * Call this after mutations that affect dashboard data.
 * Triggers revalidation without clearing data for smooth animations.
 */
export function invalidateDashboard(businessId: string) {
  mutate(dashboardKey(businessId));
}

/**
 * Update dashboard cache with new data without refetching.
 * Useful for optimistic updates.
 */
export function updateDashboardCache(
  businessId: string,
  updater: (current: DashboardData | undefined) => DashboardData | undefined
) {
  mutate(dashboardKey(businessId), updater, { revalidate: false });
}
